import { useRequiredContext } from "@redotech/react-util/context";
import { useHandler } from "@redotech/react-util/hook";
import { useTranslation } from "@redotech/redo-i18n";
import AlertTriangleIcon from "@redotech/redo-icons/alert-triangle.svg";
import ArrowLeftIcon from "@redotech/redo-icons/arrow-left.svg";
import PercentIcon from "@redotech/redo-icons/percent-01_filled.svg";
import PlusIcon from "@redotech/redo-icons/plus.svg";
import Trash01Icon from "@redotech/redo-icons/trash-01.svg";
import { RedoMarketingRpcClientContext } from "@redotech/redo-merchant-app-common/marketing-rpc-client-provider";
import {
  AutomationOutreachSplitWithTreatments,
  Treatment,
} from "@redotech/redo-merchant-marketing-rpc/schema/automation-outreach-splits/get-automation-outreach-split";
import { AutomationOutreachStepType } from "@redotech/redo-storefront-widget-common/split";
import { toast } from "@redotech/redo-web/alert";
import { RedoButton } from "@redotech/redo-web/arbiter-components/buttons/redo-button";
import { RedoTextInput } from "@redotech/redo-web/arbiter-components/input/redo-text-input";
import { Flex } from "@redotech/redo-web/flex";
import {
  InspectorField,
  InspectorLayout,
  InspectorSection,
} from "@redotech/redo-web/inspector-v2";
import { Text } from "@redotech/redo-web/text";
import { ObjectId } from "bson";
import classNames from "classnames";
import { memo, useCallback, useMemo, useRef, useState } from "react";
import * as variantStyles from "./common/split-variant-controls.module.css";
import { VariantPlaceholder } from "./common/variant-placeholders";

const MAX_VARIANTS = 6;

const VARIANT_COLOR_CLASSES = [
  "variantColor1",
  "variantColor2",
  "variantColor3",
  "variantColor4",
  "variantColor5",
  "variantColor6",
] as const;

function getVariantColorClass(
  styles: Record<string, string>,
  index: number,
): string | undefined {
  const colorKey = VARIANT_COLOR_CLASSES[index];
  return colorKey ? styles[colorKey] : undefined;
}

export interface AutomationAbTestEditorProps {
  splitWithTreatments: AutomationOutreachSplitWithTreatments;
  onClose: () => void;
  onEditVariant: (treatment: Treatment) => void;
  onAddVariant: () => void;
  renderVariantPreview?: (treatment: Treatment) => React.ReactNode;
  title?: string;
  icon?: React.ReactNode;
  stepType: AutomationOutreachStepType;
}

export const AutomationAbTestEditor = memo(function AutomationAbTestEditor({
  splitWithTreatments,
  onClose,
  onEditVariant,
  onAddVariant,
  renderVariantPreview,
  title,
  icon,
  stepType,
}: AutomationAbTestEditorProps) {
  const { t } = useTranslation();
  const marketingRpcClient = useRequiredContext(RedoMarketingRpcClientContext);
  const [testName, setTestName] = useState(
    splitWithTreatments.split.name ?? "",
  );
  const [treatments, setTreatments] = useState<Treatment[]>(
    () => splitWithTreatments.treatments,
  );
  const [isSaving, setIsSaving] = useState(false);

  const splitId = splitWithTreatments.split._id;
  const isSplitActive = splitWithTreatments.split.active;

  // Keep a ref to latest treatments for use in callbacks
  const treatmentsRef = useRef(treatments);
  treatmentsRef.current = treatments;

  const totalPercentage = useMemo(
    () => treatments.reduce((sum, t) => sum + t.percentage, 0),
    [treatments],
  );

  const canSave = useMemo(
    () => Math.abs(totalPercentage - 100) <= 0.01,
    [totalPercentage],
  );

  const canStart = useMemo(() => {
    if (!canSave) {
      return false;
    }
    return treatments.every((entry) => entry.isControl || entry.templateId);
  }, [canSave, treatments]);

  const sortedTreatments = useMemo(
    () =>
      [...treatments].sort((a, b) =>
        a.templateName.localeCompare(b.templateName),
      ),
    [treatments],
  );

  const hasControl = useMemo(
    () => treatments.some((entry) => entry.isControl),
    [treatments],
  );

  const canRemoveTreatments = treatments.length > 1;
  const isOverLimit = totalPercentage > 100;

  // Save treatments to database
  const saveTreatments = useCallback(
    async (treatmentsOverride?: Treatment[]): Promise<boolean> => {
      const treatmentsToSave = treatmentsOverride ?? treatments;

      try {
        await marketingRpcClient.updateAutomationOutreachSplit({
          splitId,
          name: testName,
          treatments: treatmentsToSave.map((entry) => ({
            percentage: entry.percentage,
            isControl: entry.isControl,
            templateId: entry.templateId,
            name: entry.templateName,
          })),
        });
        return true;
      } catch {
        toast(t("Failed to save A/B test"), { variant: "error" });
        return false;
      }
    },
    [marketingRpcClient, splitId, treatments, testName, t],
  );

  const handlePercentageChange = useCallback(
    (id: string, percentageStr: string) => {
      const percentage = parseInt(percentageStr, 10) || 0;
      setTreatments((prev) =>
        prev.map((entry) =>
          entry._id === id ? { ...entry, percentage } : entry,
        ),
      );
    },
    [],
  );

  const handleBalance = useHandler(() => {
    const count = treatments.length;
    if (count === 0) {
      return;
    }

    const equalShare = Math.floor(100 / count);
    const remainder = 100 - equalShare * count;

    setTreatments((prev) =>
      prev.map((entry, i) => ({
        ...entry,
        percentage: equalShare + (i < remainder ? 1 : 0),
      })),
    );
  });

  const handleAddControl = useHandler(async () => {
    const controlTreatment: Treatment = {
      _id: new ObjectId().toString(),
      templateName: "Control",
      percentage: 0,
      isControl: true,
    };

    const newTreatments = [...treatmentsRef.current, controlTreatment];
    setTreatments(newTreatments);
    await saveTreatments(newTreatments);
  });

  const handleRemoveTreatment = useHandler(async (id: string) => {
    const newTreatments = treatmentsRef.current.filter(
      (entry) => entry._id !== id,
    );
    setTreatments(newTreatments);
    void saveTreatments(newTreatments);
  });

  const handleEditVariant = useHandler(async (treatment: Treatment) => {
    if (!treatment.templateId) {
      return;
    }
    setIsSaving(true);
    await saveTreatments();
    setIsSaving(false);
    onEditVariant(treatment);
  });

  const handleSave = useCallback(async () => {
    if (!canSave) {
      return;
    }

    setIsSaving(true);
    const success = await saveTreatments();
    if (success) {
      onClose();
    }
    setIsSaving(false);
  }, [canSave, saveTreatments, onClose]);

  const handleStartTest = useHandler(async () => {
    if (!canStart) {
      return;
    }

    setIsSaving(true);

    try {
      await saveTreatments();
      await marketingRpcClient.startAutomationOutreachSplit({ splitId });
      toast(t("A/B test started"), { variant: "success" });
      onClose();
    } catch {
      toast(t("Failed to start A/B test"), { variant: "error" });
    } finally {
      setIsSaving(false);
    }
  });

  return (
    <InspectorLayout>
      <Flex dir="column" gap="lg">
        <Flex>
          <RedoButton
            hierarchy="tertiary"
            IconLeading={ArrowLeftIcon}
            onClick={onClose}
            size="sm"
            text={t("Back")}
          />
        </Flex>

        <Flex align="center" gap="sm">
          {icon}
          <Text fontSize="md" fontWeight="semibold">
            {title ?? t("A/B test")}
          </Text>
        </Flex>
      </Flex>

      <InspectorSection label={t("Test")}>
        <InspectorField label={t("Name")}>
          <RedoTextInput
            placeholder={t("Add a name")}
            setValue={setTestName}
            size="sm"
            state={isSplitActive ? "disabled" : undefined}
            value={testName}
          />
        </InspectorField>
      </InspectorSection>

      <InspectorSection label={t("Variants")}>
        <Flex dir="column" gap="lg" w="full">
          {sortedTreatments.map((treatment) => (
            <Flex dir="column" gap="sm" key={treatment._id} w="full">
              <Flex align="center" justify="space-between" w="full">
                <Text fontSize="sm" fontWeight="medium">
                  {treatment.templateName || t("[Variant name]")}
                </Text>
                <Flex align="center" gap="sm">
                  {!treatment.isControl && treatment.templateId && (
                    <RedoButton
                      disabled={isSplitActive}
                      hierarchy="secondary"
                      onClick={() => handleEditVariant(treatment)}
                      size="xs"
                      text={t("Edit")}
                    />
                  )}
                  {canRemoveTreatments && !isSplitActive && (
                    <RedoButton
                      aria-label={t("Remove")}
                      hierarchy="tertiary"
                      IconLeading={Trash01Icon}
                      onClick={() => handleRemoveTreatment(treatment._id)}
                      size="xs"
                    />
                  )}
                </Flex>
              </Flex>

              {treatment.isControl ? (
                <VariantPlaceholder type="control" />
              ) : renderVariantPreview ? (
                renderVariantPreview(treatment)
              ) : (
                <VariantPlaceholder
                  hasTemplate={!!treatment.templateId}
                  type="template"
                />
              )}
            </Flex>
          ))}

          <Flex gap="sm" w="full">
            <RedoButton
              disabled={isSplitActive || treatments.length >= MAX_VARIANTS}
              flexGrow={1}
              hierarchy="secondary"
              IconLeading={PlusIcon}
              onClick={onAddVariant}
              size="sm"
              text={t("Variant")}
            />
            {!hasControl && stepType === "recover" && (
              <RedoButton
                disabled={isSplitActive || treatments.length >= MAX_VARIANTS}
                flexGrow={1}
                hierarchy="secondary"
                IconLeading={PlusIcon}
                onClick={handleAddControl}
                size="sm"
                text={t("Control")}
              />
            )}
          </Flex>
        </Flex>
      </InspectorSection>

      <InspectorSection label={t("Traffic")}>
        <Flex dir="column" gap="md" w="full">
          {sortedTreatments.map((treatment, index) => (
            <Flex align="center" gap="sm" key={treatment._id} w="full">
              <div
                className={getVariantColorClass(variantStyles, index)}
                style={{
                  width: "4px",
                  height: "28px",
                  borderRadius: "var(--border-radius-xs)",
                }}
              />

              <Text fontSize="sm" style={{ flex: 1 }}>
                {treatment.templateName || t("[Variant name]")}
              </Text>

              <Flex align="center" gap="xs">
                <RedoTextInput
                  className={classNames(
                    variantStyles.noSpinners,
                    variantStyles.percentageInput,
                  )}
                  inputTypeHint="number"
                  max={100}
                  min={0}
                  setValue={(val) => handlePercentageChange(treatment._id, val)}
                  size="sm"
                  state={isSplitActive ? "disabled" : undefined}
                  trailingActions={
                    <Flex align="center" justify="center">
                      <PercentIcon style={{ width: "14px", height: "14px" }} />
                    </Flex>
                  }
                  value={treatment.percentage.toString()}
                />
              </Flex>
            </Flex>
          ))}

          <RedoButton
            disabled={isSplitActive}
            hierarchy="tertiary"
            onClick={handleBalance}
            size="sm"
            text={t("Balance")}
          />

          {totalPercentage !== 100 && (
            <Flex
              align="center"
              bgColor={isOverLimit ? "error-primary" : "warning-primary"}
              borderRadius="md"
              dir="row"
              gap="sm"
              p="sm"
              textColor={isOverLimit ? "error" : "warning"}
            >
              <AlertTriangleIcon style={{ width: "16px", height: "16px" }} />
              <Text fontSize="xs">
                {isOverLimit
                  ? t(
                      "Variations exceed 100% (currently {{totalPercentage}}%)",
                      { totalPercentage },
                    )
                  : t(
                      "Variations less than 100% (currently {{totalPercentage}}%)",
                      { totalPercentage },
                    )}
              </Text>
            </Flex>
          )}

          {/* Traffic visualization bar */}
          <Flex dir="column" gap="xs" mt="sm">
            <Flex
              dir="row"
              gap="none"
              style={{
                height: "12px",
                overflow: "visible",
                width: "100%",
                borderRadius: "var(--border-radius-sm)",
                position: "relative",
              }}
            >
              {sortedTreatments.map((treatment, index) => (
                <Flex
                  className={getVariantColorClass(variantStyles, index)}
                  key={treatment._id}
                  style={{
                    width: `${Math.min(treatment.percentage, isOverLimit ? treatment.percentage * (100 / totalPercentage) : treatment.percentage)}%`,
                    height: "100%",
                  }}
                />
              ))}
              {isOverLimit && (
                <div
                  style={{
                    position: "absolute",
                    left: `${(100 / totalPercentage) * 100}%`,
                    top: 0,
                    bottom: 0,
                    width: "2px",
                    backgroundColor: "var(--redo-colors-gray-900)",
                  }}
                />
              )}
            </Flex>

            <Flex justify="space-between" w="full">
              <Text fontSize="xxs" textColor="secondary">
                0%
              </Text>
              <Text fontSize="xxs" textColor="secondary">
                100%
              </Text>
              {isOverLimit && (
                <Text fontSize="xxs" textColor="error">
                  {totalPercentage}%
                </Text>
              )}
            </Flex>
          </Flex>
        </Flex>
      </InspectorSection>

      <Flex dir="column" gap="md" w="full">
        {isSplitActive ? (
          <RedoButton
            disabled={!canSave || isSaving}
            hierarchy="primary"
            onClick={handleSave}
            size="md"
            text={isSaving ? t("Saving...") : t("Save changes")}
          />
        ) : (
          <RedoButton
            disabled={!canStart || isSaving}
            hierarchy="primary"
            onClick={handleStartTest}
            size="md"
            text={isSaving ? t("Saving...") : t("Start test")}
          />
        )}
        <Text fontSize="xxs" textAlign="center" textColor="secondary">
          {t("Once started, variants cannot be edited")}
        </Text>
      </Flex>
    </InspectorLayout>
  );
});
